import type { WalkbookListItem, WalkbookListResponse } from '@signalvote/shared';
import type { createWalkbooksEndpoints } from '../generated/endpoints.gen';

export type WalkbookListFilter = Omit<
  NonNullable<Parameters<ReturnType<typeof createWalkbooksEndpoints>['getWalkbooks']>[0]>,
  'page' | 'pageSize'
>;

// The server clamps the walkbook list's pageSize at this value.
const MAX_WALKBOOK_PAGE_SIZE = 100;

/**
 * Lists every walkbook matching the filters, reading page after page until the server's
 * totalCount is reached. A turf cut or archived mid-read shifts the pages, repeating one row and
 * skipping another, so repeats are dropped and a short list warns instead of passing as complete.
 */
export function createListAllWalkbooks(walkbooks: Pick<ReturnType<typeof createWalkbooksEndpoints>, 'getWalkbooks'>) {
  return async (filters?: WalkbookListFilter): Promise<WalkbookListResponse> => {
    const byId = new Map<string, WalkbookListItem>();
    let totalCount = 0;
    for (let page = 1; ; page++) {
      const response = await walkbooks.getWalkbooks({ ...filters, page, pageSize: MAX_WALKBOOK_PAGE_SIZE });
      totalCount = response.totalCount;
      const sizeBefore = byId.size;
      for (const item of response.items) byId.set(item.id, item);
      if (byId.size === sizeBefore || byId.size >= totalCount) break;
    }
    const items = [...byId.values()];
    if (items.length < totalCount) {
      console.warn(`walkbooks.listAll: received ${items.length} of ${totalCount} walkbooks`);
    }
    return { items, totalCount, page: 1, pageSize: items.length };
  };
}
